import createAuth0Client from '@auth0/auth0-spa-js';

// Auth0 implementation of the client auth contract (see ./index.js).
// This is the previous Auth0ClientService logic, with the audience lifted out
// of the three places it was hardcoded and into config.

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const config = () => {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    if (!domain || !clientId || !audience) {
        throw new Error(
            '[auth] provider=auth0 requires REACT_APP_AUTH0_DOMAIN, ' +
            'REACT_APP_AUTH0_CLIENT_ID and REACT_APP_AUTH0_AUDIENCE.',
        );
    }
    return { domain, clientId, audience };
};

const newClient = () => {
    const { domain, clientId, audience } = config();
    return createAuth0Client({
        domain,
        client_id: clientId,
        audience,
        redirect_uri: `${window.location.origin}`,
        authorizationParams: { redirect_uri: `${window.location.origin}` },
    });
};

let clientPromise = null;
const client = () => {
    if (!clientPromise) clientPromise = newClient();
    return clientPromise;
};

const createAuth0AuthProvider = () => ({
    name: 'auth0',

    resetClient() {
        clientPromise = newClient();
    },

    // The redirect flow returns before the SDK has finished restoring the
    // session, so poll briefly rather than reporting a signed-in user as
    // anonymous. Preserved from the original implementation.
    async getUser() {
        let user = null;
        let retries = 7;
        while (!user && retries > 0) {
            retries -= 1;
            const auth0 = await client();
            user = await auth0.getUser();
            if (user) return { ...user, user_id: user.sub };
            if (retries > 0) await delay(70);
        }
        return null;
    },

    async login(redirectPath = '') {
        const auth0 = await client();
        await auth0.loginWithRedirect({
            redirect_uri: `${window.location.origin}${redirectPath}`,
        });
    },

    async logout() {
        const auth0 = await client();
        await auth0.logout({ returnTo: window.location.origin });
    },

    async getAuthToken() {
        const { audience } = config();
        const auth0 = await client();
        try {
            return await auth0.getTokenSilently({
                audience,
                redirect_uri: `${window.location.origin}`,
            });
        } catch (error) {
            if (error?.error === 'login_required') return null;
            return auth0.getTokenWithPopup({
                audience,
                redirect_uri: `${window.location.origin}`,
            });
        }
    },
});

export default createAuth0AuthProvider;
